import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { APIResponse, BasicResponse } from '../_types/api-responses.types';
import { Lead, UpdateLeadStatusRequest } from '../_types/lead.types';
import {
  AcceptOfferRequest,
  AllTasksRequest,
  AllTasksResponse,
  CreateLeadResponse,
  ParseOfferPdfRequest,
  ParseOfferPdfResponse,
  ProposalDetails,
  ProposalFetchRequest,
  SendProposalsRequest,
  UpdateCentralisationStatusRequest,
  UpdatePolicyRequest,
  UpdatePolicyResponse,
  UpdatePolicyStatusRequest,
  UpsertLeadRequest,
} from '../_types/proposals.type';
import { EntityType } from '../enums/entity-type.enum';
import { ProposalState } from '../enums/proposal.enums';
import { Globals } from '../globals';

@Injectable({
  providedIn: 'root',
})
export class ProposalService {
  _baseURL: any;

  user: any;

  deviceId: number;

  constructor(
    private httpClient: HttpClient,
    private globaols: Globals
  ) {
    this.deviceId = Date.now();
    this.user = this.globaols.getUser();
    this._baseURL = environment.apiUrl;
  }

  get headers() {
    return new HttpHeaders().set('Access-Token', this.globaols.getUser().token);
  }

  get fileUploadHeaders(): HttpHeaders {
    return new HttpHeaders().set('Access-Token', this.globaols.getUser().token);
  }

  _addProposal(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/add/proposal`,
      obj,
      { headers: this.headers }
    );
  }

  _updateProposal(obj: any): Observable<any> {
    return this.httpClient.put(
      `${this._baseURL}api/insurance/v1/update/proposal`,
      obj,
      { headers: this.headers }
    );
  }

  _getPartnerProposalList(
    obj: ProposalFetchRequest,
    fetchDocuments: boolean = false
  ): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/proposals`,
      { ...obj, fetchDocuments },
      { headers: this.headers }
    );
  }

  _getPartnerProposalDetails(
    obj: any
  ): Observable<APIResponse<ProposalDetails>> {
    return this.httpClient.post<APIResponse<ProposalDetails>>(
      `${this._baseURL}api/insurance/v1/get/proposal`,
      obj,
      { headers: this.headers }
    );
  }

  _validatePolicyId(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/validate/policy-id`,
      obj,
      { headers: this.headers }
    );
  }

  _validateOfferId(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/validate/offer-id`,
      obj,
      { headers: this.headers }
    );
  }

  _getStaticData(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/static/data`,
      obj,
      { headers: this.headers }
    );
  }

  _getPartnerCustomerLeadList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/leads`,
      obj,
      { headers: this.headers }
    );
  }

  _getCustomerLeadList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/user/leads`,
      obj,
      { headers: this.headers }
    );
  }

  _getPartnerLeadDetails(obj: any): Observable<APIResponse<Lead>> {
    return this.httpClient.post<APIResponse<Lead>>(
      `${this._baseURL}api/insurance/v1/get/lead/details`,
      {
        ...obj,
        entityType: EntityType.PARTNER_ID,
        entityId: this.user.partnerId,
      },
      { headers: this.headers }
    );
  }

  getLeadDetailsByFlowId(obj: {
    flowId: string;
  }): Observable<APIResponse<Lead>> {
    return this.httpClient.post<APIResponse<Lead>>(
      `${this._baseURL}api/insurance/v1/get/lead/details/flow-id`,
      { ...obj },
      {
        headers: this.headers,
      }
    );
  }

  _getPartnerLeadProposalList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/lead/proposals`,
      obj,
      { headers: this.headers }
    );
  }

  _updateProposalState(obj: {
    proposalId: string;
    proposalState: ProposalState;
  }): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/update/proposal/state`,
      obj,
      { headers: this.headers }
    );
  }

  _deleteSavedLeadProposal(proposalIds: string[]): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/delete/proposal`,
      { proposalIds },
      { headers: this.headers }
    );
  }

  _getProposalSubmissionList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/submissions`,
      obj,
      { headers: this.headers }
    );
  }

  _sendProposal(obj: SendProposalsRequest): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/send/proposal`,
      obj,
      { headers: this.headers }
    );
  }

  _addPolicyId(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/add/policy-id`,
      obj,
      { headers: this.headers }
    );
  }

  _getCustomerContractList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/user/proposal`,
      obj,
      { headers: this.headers }
    );
  }

  _getOverallStatisticsList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/overall/statistics`,
      obj,
      { headers: this.headers }
    );
  }

  _getCustomerStatisticsList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/customers/statistics`,
      obj,
      { headers: this.headers }
    );
  }

  _getAgentStatisticsList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/agents/statistics`,
      obj,
      { headers: this.headers }
    );
  }

  _getAnalysisList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/analysis`,
      obj,
      { headers: this.headers }
    );
  }

  download(url: string): Observable<Blob> {
    return this.httpClient.get(url, {
      responseType: 'blob',
    });
  }

  _getDashboardInfo(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/partner/dashboard-info`,
      obj,
      { headers: this.headers }
    );
  }

  _getExpiredContractList(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/get/expired/proposals`,
      obj,
      { headers: this.headers }
    );
  }

  _addExistingContract(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/add/existing/contract`,
      obj,
      { headers: this.headers }
    );
  }

  _modifyContractCOmmission(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/modify/commission`,
      obj,
      { headers: this.headers }
    );
  }

  _assignAgentToLead(obj: any): Observable<any> {
    return this.httpClient.post(
      `${this._baseURL}api/insurance/v1/assign/partner-agent`,
      obj,
      { headers: this.headers }
    );
  }

  updateLeadStatus(
    obj: UpdateLeadStatusRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/update/lead/status`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  createLead(obj: UpsertLeadRequest) {
    return this.httpClient.post<APIResponse<CreateLeadResponse>>(
      `${this._baseURL}api/insurance/v1/request/proposal`,
      obj,
      {
        headers: this.headers,
      }
    );
  }

  updateCentralisationTrackingStatus(
    request: UpdateCentralisationStatusRequest
  ): Observable<APIResponse<BasicResponse>> {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/update/centralisation/status`,
      request,
      { headers: this.headers }
    );
  }

  getAllTasks(obj: AllTasksRequest): Observable<APIResponse<AllTasksResponse>> {
    return this.httpClient.post<APIResponse<AllTasksResponse>>(
      `${this._baseURL}api/insurance/v1/get/tasks`,
      obj,
      { headers: this.headers }
    );
  }

  parseOfferPdf(
    request: ParseOfferPdfRequest
  ): Observable<APIResponse<ParseOfferPdfResponse>> {
    const form = new FormData();
    form.append('doc', request.doc);
    form.append('insuranceType', request.insuranceType);

    return this.httpClient.post<APIResponse<ParseOfferPdfResponse>>(
      `${this._baseURL}api/insurance/v1/offer/parse`,
      form,
      {
        headers: new HttpHeaders().set(
          'Access-Token',
          this.globaols?.getUser()?.token
        ),
      }
    );
  }

  updatePolicy(request: UpdatePolicyRequest) {
    return this.httpClient.put<APIResponse<UpdatePolicyResponse>>(
      `${this._baseURL}api/insurance/v1/update/policy`,
      request,
      { headers: this.headers }
    );
  }

  /**
   * Appends one PDF, typed POLICY, to an accepted offer or a policy
   * (OFFER_ACCEPTED, SIGNED_DOCS or POLICY_CREATED); the flow's history logs
   * it. The answer carries the new document's id, which the caller does not read.
   */
  addPolicyDocument(
    proposalId: string,
    file: File
  ): Observable<APIResponse<number>> {
    const form = new FormData();
    form.append('file', file, file.name);

    return this.httpClient.post<APIResponse<number>>(
      `${this._baseURL}api/insurance/v1/proposal/${proposalId}/documents`,
      form,
      { headers: this.fileUploadHeaders }
    );
  }

  acceptOffer(
    request: AcceptOfferRequest
  ): Observable<APIResponse<BasicResponse>> {
    const form = new FormData();
    form.append('proposalId', request.proposalId);
    form.append(
      'signatureDate',
      request.signatureDate.toISOString()?.split('T')?.[0]
    );
    form.append('offerDocument', request.offerDocument);

    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/accept/offer`,
      form,
      {
        headers: this.fileUploadHeaders,
      }
    );
  }

  patchLead(request: UpsertLeadRequest) {
    return this.httpClient.post<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/patch/lead`,
      request,
      { headers: this.headers }
    );
  }

  updatePolicyStatus(request: UpdatePolicyStatusRequest) {
    return this.httpClient.put<APIResponse<BasicResponse>>(
      `${this._baseURL}api/insurance/v1/update/policy-status`,
      request,
      { headers: this.headers }
    );
  }
}
